import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { routingConfig } from '@makemydeal/dr-sales-checkin-types';
import type { DealType, DealVisibility, IncentiveType } from '@makemydeal/dr-shared-bff-types';
import { setWorkflowEvaluation } from '@makemydeal/dr-sales-checkin-store';
import {
    resetRequestProposalStatus,
    toggleDealType,
    toggleIncentive,
    updateNotes,
    showAcceptDealLoader,
    setShowConfetti,
    requestProposalSendRequestClicked,
    requestProposalAcceptDealClicked,
    showSendDealRequestLoader,
    middlewareMgr
} from '../store';
import { getAllRoutes } from '../utils/routes';

/**
 * Action/navigation handlers for the request-proposal (deal-summary) workflow.
 * Screen-specific concerns live in their own hooks: PDF retrieval in useDealSummaryPdf,
 * Request Changes modal visibility in useRequestChangeModal, workflow-status navigation
 * in useInitializeWorkflowNavigation.
 */
export const useRequestProposalActions = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { requestQuote: _requestQuote, viewQuote } = getAllRoutes(routingConfig.DEAL_SUMMARY);

    const handleToggleDealType = useCallback(
        (dealTypes: DealType[]) => {
            dispatch(toggleDealType(dealTypes));
        },
        [dispatch]
    );

    const handleToggleIncentive = useCallback(
        (incentives: IncentiveType[]) => {
            dispatch(toggleIncentive(incentives));
        },
        [dispatch]
    );

    const handleNotesChange = useCallback(
        (value: string) => {
            dispatch(updateNotes(value));
        },
        [dispatch]
    );

    const handleSendRequest = useCallback(
        (payload: DealVisibility) => {
            dispatch(showSendDealRequestLoader());
            dispatch(middlewareMgr.hostActions.updateActiveDealVisibilityStatus(payload));
            dispatch(middlewareMgr.hostActions.updateDealSummary());
        },
        [dispatch]
    );

    const handleRequestChanges = useCallback(() => {
        dispatch(resetRequestProposalStatus());
        history.push({ pathname: viewQuote, search: history.location.search });
    }, [history, viewQuote]);

    const handleAcceptDeal = useCallback(() => {
        dispatch(setWorkflowEvaluation(true));
        dispatch(requestProposalAcceptDealClicked());
        dispatch(showAcceptDealLoader());
        dispatch(setShowConfetti(true));
        dispatch(middlewareMgr.hostActions.setDealAcceptedByBuyer());
        dispatch(middlewareMgr.hostActions.updateDealSummary());
    }, [dispatch]);

    const onSendRequestClick = () => {
        const visibilityPayload: DealVisibility = { needsManagerApproval: true, approvedByManager: false, isDraftDeal: false };
        dispatch(requestProposalSendRequestClicked());
        handleSendRequest(visibilityPayload);
    };

    return {
        handleToggleDealType,
        handleToggleIncentive,
        handleNotesChange,
        handleSendRequest,
        handleRequestChanges,
        handleAcceptDeal,
        onSendRequestClick
    };
};
